// src/pages/ProjectSelector.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { buildVisibleProjects } from "../utils/projectVisibility";

const ProjectSelector = () => {
  const navigate = useNavigate();
  const { role, userPermissions, loading } = useAuth();

  const [aracTakipMap, setAracTakipMap] = useState(null);
  const [personelMap, setPersonelMap] = useState(null);
  const [mazotMap, setMazotMap] = useState(null);
  const [kisiRaporMap, setKisiRaporMap] = useState(null);
  const [hazirBetonMap, setHazirBetonMap] = useState(null);
  const [cimentoOperasyonuMap, setCimentoOperasyonuMap] = useState(null);
  const [filoTakipMap, setFiloTakipMap] = useState(null);
  const [kontrolFormlariMap, setKontrolFormlariMap] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (loading) return;

    const needAracTakip = !userPermissions?.["arac-takip"] && !userPermissions?.aractakip;
    const needPersonel = !userPermissions?.personeltakip;
    const needMazot = !userPermissions?.["mazot-takip"];
    const needKisiRapor = !userPermissions?.["kisiye-ozel-rapor"];
    const needHazirBeton = !userPermissions?.["hazir-beton"];
    const needCimentoOperasyonu = !userPermissions?.["cimento-operasyonu"];
    const needFiloTakip = !userPermissions?.filotakip;
    const needKontrolFormlari = !userPermissions?.["kontrol-formlari"];

    if (
      !needAracTakip &&
      !needPersonel &&
      !needMazot &&
      !needKisiRapor &&
      !needHazirBeton &&
      !needCimentoOperasyonu &&
      !needFiloTakip &&
      !needKontrolFormlari
    ) {
      return;
    }

    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    (async () => {
      try {
        setFetching(true);
        let snap = null;
        if (currentUser.email) snap = await getDoc(doc(db, "users", currentUser.email));
        if (!snap || !snap.exists()) snap = await getDoc(doc(db, "users", currentUser.uid));

        if (snap && snap.exists()) {
          const data = snap.data() || {};
          if (needAracTakip) setAracTakipMap(data["arac-takip"] || data.aractakip || {});
          if (needPersonel) setPersonelMap(data.personeltakip || {});
          if (needMazot) setMazotMap(data["mazot-takip"] || {});
          if (needKisiRapor) setKisiRaporMap(data["kisiye-ozel-rapor"] || {});
          if (needHazirBeton) setHazirBetonMap(data["hazir-beton"] || {});
          if (needCimentoOperasyonu) setCimentoOperasyonuMap(data["cimento-operasyonu"] || {});
          if (needFiloTakip) setFiloTakipMap(data.filotakip || {});
          if (needKontrolFormlari) setKontrolFormlariMap(data["kontrol-formlari"] || {});
        }
      } finally {
        setFetching(false);
      }
    })();
  }, [loading, userPermissions]);

  const projects = useMemo(
    () => buildVisibleProjects({
      role,
      userPermissions,
      fallbacks: {
        aracTakipMap,
        personelMap,
        mazotMap,
        kisiRaporMap,
        hazirBetonMap,
        cimentoOperasyonuMap,
        filoTakipMap,
        kontrolFormlariMap,
      },
    }),
    [
      role,
      userPermissions,
      aracTakipMap,
      personelMap,
      mazotMap,
      kisiRaporMap,
      hazirBetonMap,
      cimentoOperasyonuMap,
      filoTakipMap,
      kontrolFormlariMap,
    ]
  );

  const handleSelect = (path) => navigate(path);

  useEffect(() => {
    if (!loading && !fetching && projects.length === 1) navigate(projects[0].path, { replace: true });
  }, [loading, fetching, projects, navigate]);

  if (loading || fetching) {
    return <div style={{ textAlign: "center", marginTop: "2rem" }}>Yükleniyor...</div>;
  }

  if (!projects.length) {
    return <div style={{ textAlign: "center", marginTop: "2rem" }}>Yetki bulunamadı.</div>;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f8fafc",
        padding: "20px",
      }}
    >
      <h1
        style={{
          fontSize: "24px",
          fontWeight: "bold",
          marginBottom: "24px",
          color: "#334155",
        }}
      >
        Lütfen bir proje seçin
      </h1>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          width: "100%",
          maxWidth: "600px",
        }}
      >
        {projects.map((project) => (
          <div
            key={project.name}
            onClick={() => handleSelect(project.path)}
            style={{
              cursor: "pointer",
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              textAlign: "center",
              fontWeight: "600",
              color: "#1e293b",
              transition: "box-shadow 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)")
            }
          >
            {project.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectSelector;
